import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Target,
  Brain,
  Zap,
  Heart,
  Shield,
  Save
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function IkonsPreMatch() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const totalSteps = 6;

  // Match Plan state
  const [matchPlan, setMatchPlan] = useState({
    match_date: new Date().toISOString().split('T')[0],
    rival: '',
    main_objective: '',
    key_action: '',
    mental_trigger: '',
    confidence_phrase: ''
  });

  // Performance Map state (1-5 scale)
  const [performanceMap, setPerformanceMap] = useState({
    match_date: new Date().toISOString().split('T')[0],
    physical_energy: 3,
    mental_clarity: 3,
    emotional_state: 3,
    confidence: 3,
    competitive_level: 3
  });

  // Auto-save draft
  const saveDraft = useCallback(() => {
    const draft = { matchPlan, performanceMap };
    localStorage.setItem(`prematch_draft_${matchPlan.match_date}`, JSON.stringify(draft));
    setLastSaved(new Date());
  }, [matchPlan, performanceMap]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const savedDraft = localStorage.getItem(`prematch_draft_${today}`);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setMatchPlan(draft.matchPlan || matchPlan);
        setPerformanceMap(draft.performanceMap || performanceMap);
        toast.info('Borrador recuperado');
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(saveDraft, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  const handleNext = () => {
    saveDraft();
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    saveDraft();
    if (step > 1) setStep(step - 1);
    else navigate('/ikons');
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/ikons/match-plans', matchPlan);
      // Ensure performance map uses the same match_date as the match plan
      await api.post('/ikons/performance-maps', {
        ...performanceMap,
        match_date: matchPlan.match_date
      });
      localStorage.removeItem(`prematch_draft_${matchPlan.match_date}`);
      toast.success('Pre-partido guardado');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const getReadinessScore = () => {
    const values = Object.values(performanceMap);
    const total = values.reduce((a, b) => a + b, 0);
    return Math.round((total / 25) * 100);
  };

  const readiness = getReadinessScore();

  // Rating buttons component (better for mobile)
  const MetricRating = ({ value, onChange, label, icon: Icon, color }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="font-medium text-[#4F4F4F] flex-1">{label}</span>
        <span className="text-2xl font-bold text-[#4F4F4F]">{value}</span>
      </div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`flex-1 h-14 rounded-xl font-bold text-lg transition-all active:scale-95 ${
              value === num 
                ? 'bg-[#D4AF37] text-white shadow-lg' 
                : 'bg-gray-100 text-[#808080] hover:bg-gray-200'
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-[#808080] px-1">
        <span>Bajo</span>
        <span>Óptimo</span>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="ikons-pre-match">
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-white border-b border-gray-100 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <button onClick={handleBack} className="p-2 -ml-2 text-[#808080] active:scale-95">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <span className="text-xs text-[#808080] uppercase tracking-wider">Pre-Partido</span>
                <p className="text-sm text-[#4F4F4F] font-medium">Paso {step} de {totalSteps}</p>
              </div>
              <div className="w-9" />
            </div>
            
            {/* Progress bar */}
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    i < step ? 'bg-[#D4AF37]' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Auto-save indicator */}
        {lastSaved && (
          <div className="px-4 pt-4 max-w-lg mx-auto">
            <div className="flex items-center gap-2 text-xs text-[#808080]">
              <Save className="w-3 h-3" />
              <span>Guardado: {lastSaved.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 py-6 max-w-lg mx-auto pb-32">
          
          {/* Step 1: Match Info */}
          {step === 1 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">PREPARACIÓN</h1>
                <p className="text-[#808080]">Información del partido</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-4">
                <div>
                  <label className="text-sm text-[#808080] mb-2 block">Fecha del partido</label>
                  <Input
                    type="date"
                    value={matchPlan.match_date}
                    onChange={(e) => setMatchPlan({ ...matchPlan, match_date: e.target.value })}
                    className="h-12 bg-white border-gray-200 text-[#4F4F4F]"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#808080] mb-2 block">Rival</label>
                  <Input
                    value={matchPlan.rival}
                    onChange={(e) => setMatchPlan({ ...matchPlan, rival: e.target.value })}
                    placeholder="Nombre del equipo rival"
                    className="h-12 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Objective */}
          {step === 2 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">OBJETIVO</h1>
                <p className="text-[#808080]">¿Qué quieres lograr hoy?</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <Textarea
                  value={matchPlan.main_objective}
                  onChange={(e) => setMatchPlan({ ...matchPlan, main_objective: e.target.value })}
                  placeholder="Ej: Dominar el mediocampo, ser el primero en cada dividida..."
                  rows={4}
                  className="bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400 text-base resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Key Action */}
          {step === 3 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">ACCIÓN CLAVE</h1>
                <p className="text-[#808080]">Una acción específica en la que enfocarte</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                <Input
                  value={matchPlan.key_action}
                  onChange={(e) => setMatchPlan({ ...matchPlan, key_action: e.target.value })}
                  placeholder="Ej: Primer toque orientado hacia adelante"
                  className="h-14 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400"
                />
              </div>

              <div className="bg-[#D4AF37]/10 p-4 rounded-xl border border-[#D4AF37]/20">
                <p className="text-sm text-[#4F4F4F]">
                  <strong className="text-[#D4AF37]">Tip:</strong> Elige algo concreto y observable. "Jugar bien" es vago. "Mirar antes de recibir" es específico.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Mental Trigger */}
          {step === 4 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">GATILLO MENTAL</h1>
                <p className="text-[#808080]">Palabra o gesto que te active</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-4">
                <Input
                  value={matchPlan.mental_trigger}
                  onChange={(e) => setMatchPlan({ ...matchPlan, mental_trigger: e.target.value })}
                  placeholder="Ej: ¡VAMOS!, golpe en el pecho..."
                  className="h-14 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400"
                />
                <Input
                  value={matchPlan.confidence_phrase}
                  onChange={(e) => setMatchPlan({ ...matchPlan, confidence_phrase: e.target.value })}
                  placeholder="Frase de confianza: Soy imparable..."
                  className="h-14 bg-white border-gray-200 text-[#4F4F4F] placeholder:text-gray-400"
                />
              </div>
            </div>
          )}

          {/* Step 5: Performance Map */}
          {step === 5 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">MAPA 360°</h1>
                <p className="text-[#808080]">¿Cómo llegas al partido?</p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-8">
                <MetricRating
                  value={performanceMap.physical_energy}
                  onChange={(v) => setPerformanceMap({ ...performanceMap, physical_energy: v })}
                  label="Energía Física"
                  icon={Zap}
                  color="bg-green-500"
                />
                <MetricRating
                  value={performanceMap.mental_clarity}
                  onChange={(v) => setPerformanceMap({ ...performanceMap, mental_clarity: v })}
                  label="Claridad Mental"
                  icon={Brain}
                  color="bg-blue-500"
                />
                <MetricRating
                  value={performanceMap.emotional_state}
                  onChange={(v) => setPerformanceMap({ ...performanceMap, emotional_state: v })}
                  label="Estado Emocional"
                  icon={Heart}
                  color="bg-pink-500"
                />
                <MetricRating
                  value={performanceMap.confidence}
                  onChange={(v) => setPerformanceMap({ ...performanceMap, confidence: v })}
                  label="Confianza"
                  icon={Shield}
                  color="bg-purple-500"
                />
                <MetricRating
                  value={performanceMap.competitive_level}
                  onChange={(v) => setPerformanceMap({ ...performanceMap, competitive_level: v })}
                  label="Nivel Competitivo"
                  icon={Target}
                  color="bg-[#D4AF37]"
                />
              </div>
            </div>
          )}

          {/* Step 6: Summary */}
          {step === 6 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-[#4F4F4F] mb-2">RESUMEN</h1>
                <p className="text-[#808080]">Revisa antes de guardar</p>
              </div>

              {/* Readiness Circle */}
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64" cy="64" r="56"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="12"
                    />
                    <circle
                      cx="64" cy="64" r="56"
                      fill="none"
                      stroke={readiness >= 80 ? '#22C55E' : readiness >= 60 ? '#D4AF37' : '#EF4444'}
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={`${(readiness / 100) * 352} 352`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-[#4F4F4F]">{readiness}%</span>
                  </div>
                </div>
                <p className="text-sm text-[#808080] mt-2">Nivel de Preparación</p>
              </div>

              {/* Plan Summary */}
              {matchPlan.main_objective && (
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                  <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1">Objetivo</p>
                  <p className="text-[#4F4F4F]">{matchPlan.main_objective}</p>
                </div>
              )}

              {matchPlan.key_action && (
                <div className="bg-white p-4 rounded-xl border-l-4 border-[#D4AF37]">
                  <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1">Acción Clave</p>
                  <p className="text-[#4F4F4F] font-medium">{matchPlan.key_action}</p>
                </div>
              )}

              {matchPlan.confidence_phrase && (
                <div className="bg-[#4F4F4F] p-4 rounded-xl text-center">
                  <p className="text-white italic">"{matchPlan.confidence_phrase}"</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fixed Bottom Action */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            {step > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="h-14 px-6 border-gray-200 text-[#4F4F4F] hover:bg-gray-50"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            
            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#A67C00] text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform rounded-full"
              >
                Continuar
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 h-14 bg-[#D4AF37] hover:bg-[#A67C00] text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform rounded-full"
              >
                {loading ? 'Guardando...' : 'Guardar Pre-Partido'}
                <Check className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default IkonsPreMatch;
